import { useTranslation } from 'react-i18next';

interface Experience {
  id: string;
  title: string;
  price_from: number;
  category: string;
  group_type: string;
  image: string;
}

interface ExperiencesSectionProps {
  title: string;
  experiences: Experience[];
  bgColor?: string;
  loading?: boolean;
}

export default function ExperiencesSection({ title, experiences, bgColor = 'bg-white', loading }: ExperiencesSectionProps) {
  const { t } = useTranslation();
  return (
    <section className={`w-full ${bgColor} py-10 md:py-14`}>
      <div className="w-full px-4 md:px-8 lg:px-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-secondary-900 mb-6 md:mb-8">
          {title}
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-secondary-100 overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-secondary-100" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-secondary-100 rounded w-3/4" />
                  <div className="h-3 bg-secondary-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : experiences.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-secondary-500 text-sm">{t('experiences.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {experiences.slice(0, 6).map((exp) => (
              <a
                key={exp.id}
                href={`/experience/${exp.id}`}
                className="group block bg-white rounded-xl border border-secondary-100 overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={exp.image}
                    alt={exp.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-md px-2 py-1">
                    <span className="text-xs font-medium text-secondary-700">
                      {t('experiences.from', { price: exp.price_from })}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-medium text-secondary-900 text-sm md:text-base mb-2 line-clamp-2">
                    {exp.title}
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-secondary-100 text-secondary-600 text-xs rounded-md">
                      {exp.category}
                    </span>
                    <span className="px-2 py-0.5 bg-secondary-100 text-secondary-600 text-xs rounded-md">
                      {exp.group_type}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}